import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import Logo from '@/components/feature/Logo';

interface AuthLayoutProps {
  children: ReactNode;
  maxWidth?: string;
}

export default function AuthLayout({ children, maxWidth = 'max-w-md' }: AuthLayoutProps) {
  const { t } = useTranslation();

  const highlights = [
    { icon: 'ri-file-list-3-line', text: t('auth.brand.feature.orders') },
    { icon: 'ri-e-bike-2-line', text: t('auth.brand.feature.drivers') },
    { icon: 'ri-wallet-3-line', text: t('auth.brand.feature.finance') },
  ];

  return (
    <div className="relative min-h-screen bg-background-100 lg:grid lg:grid-cols-2">
      <div className="lb-app-glow pointer-events-none fixed inset-0 z-0" aria-hidden="true" />
      <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-10 md:px-8">
        <div className={`w-full ${maxWidth}`}>
          <div className="mb-6 flex h-28 items-center lg:hidden">
            <Logo variant="color" className="h-24" />
          </div>
          {children}
        </div>
      </div>

      <div className="relative z-10 hidden overflow-hidden bg-primary-900 lg:flex lg:sticky lg:top-0 lg:h-screen lg:flex-col lg:justify-between p-10 xl:p-14">
        <div
          className="pointer-events-none absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              'url("https://readdy.ai/api/search-image?query=Abstract%20soft%20draped%20ivory%20fabric%20waves%20on%20deep%20navy%20blue%20background%2C%20minimal%20elegant%20editorial%20photography%2C%20generous%20calm%20empty%20negative%20space%20in%20the%20center%2C%20soft%20cinematic%20lighting%2C%20no%20people%2C%20quiet%20luxurious%20mood&width=1000&height=1400&seq=auth-calm-2026-a&orientation=portrait")',
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0"
          style={{
            background:
              'linear-gradient(to top, oklch(var(--primary-950) / 0.97) 0%, oklch(var(--primary-950) / 0.86) 50%, oklch(var(--primary-900) / 0.74) 100%)',
          }}
          aria-hidden="true"
        />
        <div
          className="pointer-events-none absolute inset-0 opacity-30"
          style={{
            background:
              'radial-gradient(60% 60% at 80% 8%, oklch(var(--accent-600) / 0.28), transparent 70%), radial-gradient(50% 50% at 10% 92%, oklch(var(--primary-500) / 0.32), transparent 70%)',
          }}
          aria-hidden="true"
        />
        <div className="relative">
          <div className="flex h-24 items-start">
            <Logo variant="white" className="h-24" />
          </div>
        </div>

        <div className="relative">
          <h2 className="text-2xl font-bold leading-snug text-background-50 xl:text-3xl">
            {t('auth.login.brandTitle')}
          </h2>
          <p className="mt-2 max-w-md text-sm leading-relaxed text-primary-100">
            {t('auth.login.brandText')}
          </p>

          <ul className="mt-8 space-y-3">
            {highlights.map((item) => (
              <li key={item.icon} className="flex items-center gap-3 text-sm text-primary-100">
                <span className="flex h-9 w-9 items-center justify-center rounded-md bg-background-50/10 text-accent-300">
                  <i className={`${item.icon} text-lg leading-none`} aria-hidden="true" />
                </span>
                {item.text}
              </li>
            ))}
          </ul>
        </div>

        <p className="relative text-xs text-primary-200/80">{t('app.tagline')}</p>
      </div>
    </div>
  );
}